import { useEffect, useState } from "react";
import clsx from "clsx";
import { Dumbbell } from "lucide-react";
import type { Sport } from "@flexv-coach/shared";
import { tokenStore } from "@/lib/api";

/**
 * Media olahraga bersama (dipakai menu Sport & halaman Profile).
 *
 * `SportIcon`  — ikon dari `sports.icon_url`, diwarnai mengikuti teks
 *                (monokrom) via inline `currentColor`.
 * `SportCover` — gambar dari `sports.image_url` (cover); bila kosong/gagal
 *                mundur ke `icon_url`, lalu ikon generik.
 */

type SportIconTone = "text" | "soft";

/** Map tone → utility warna teks yang sudah adaptif light/dark. */
const SPORT_ICON_TONES: Record<SportIconTone, string> = {
    text: "app-text-primary",
    soft: "app-text-soft",
};

export function SportIcon({
    sport,
    tone = "text",
    className,
}: {
    sport: Sport;
    tone?: SportIconTone;
    className?: string;
}) {
    const [svg, setSvg] = useState<string | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        setSvg(null);
        setFailed(false);

        const url = sport.iconUrl;
        // Bukan file svg → biarkan `<img>` biasa, tanpa pewarnaan.
        // File svg sendiri DIAMBIL LEWAT PROXY API (`/sports/:id/icon.svg`),
        // bukan URL mentah — server aset tidak mengirim header CORS sehingga
        // `fetch()` langsung dari browser pasti diblokir.
        if (!url || !/\.svg($|\?)/i.test(url.split("?")[0] ?? "")) {
            if (url) setFailed(true);
            return;
        }

        const controller = new AbortController();
        fetch(`/api/sports/${sport.id}/icon.svg`, {
            headers: { Authorization: `Bearer ${tokenStore.getAccess() ?? ""}` },
            signal: controller.signal,
        })
            .then((response) => {
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                return response.text();
            })
            .then((text) => {
                // Hanya terima markup svg yang sah.
                if (!/<svg[\s>]/i.test(text)) throw new Error("not svg");
                // Hitam dasar (fill/stroke + `black` di style) → warisan warna
                // teks. `none` sengaja dipertahankan supaya bagian transparan
                // tetap transparan.
                const tinted = text
                    .replace(/(fill|stroke)="black"/gi, '$1="currentColor"')
                    .replace(/(fill|stroke)\s*:\s*black/gi, "$1:currentColor");
                setSvg(tinted);
            })
            .catch((error: unknown) => {
                // Abaikan pembatalan (ganti sport / unmount).
                if (error instanceof DOMException && error.name === "AbortError") return;
                setFailed(true);
            });

        return () => {
            controller.abort();
        };
    }, [sport.iconUrl, sport.id]);

    const shellClass = clsx("shrink-0 rounded-lg object-contain", className);

    if ((!svg && !sport.iconUrl) || failed) {
        return (
            <span
                className={clsx(
                    "app-content-subtle grid shrink-0 place-items-center rounded-lg",
                    SPORT_ICON_TONES[tone],
                    className,
                )}
            >
                <Dumbbell className="h-3.5 w-3.5" />
            </span>
        );
    }

    if (svg) {
        return (
            <span
                // SVG dari core dipercaya (aset internal Flexv), jadi
                // `dangerouslySetInnerHTML` dapat dipakai langsung.
                // ponytail: kalau suatu saat URL bisa diisi user, sanitize dulu
                // (mis. DOMPurify) sebelum di-inline.
                dangerouslySetInnerHTML={{ __html: svg }}
                className={clsx(
                    shellClass,
                    SPORT_ICON_TONES[tone],
                    "[&>svg]:h-full [&>svg]:w-full",
                )}
            />
        );
    }

    return (
        <img
            src={sport.iconUrl ?? undefined}
            alt=""
            loading="lazy"
            onError={() => setFailed(true)}
            className={shellClass}
        />
    );
}

export function SportCover({
    sport,
    size = "md",
}: {
    sport?: Sport;
    size?: "md" | "lg";
}) {
    const [imageFailed, setImageFailed] = useState(false);

    const dims =
        size === "lg"
            ? { box: "h-16 w-16 rounded-2xl", icon: "h-9 w-9", glyph: "h-6 w-6" }
            : { box: "h-14 w-14 rounded-xl", icon: "h-8 w-8", glyph: "h-5 w-5" };

    if (sport?.imageUrl && !imageFailed) {
        return (
            <img
                src={sport.imageUrl}
                alt=""
                loading="lazy"
                onError={() => setImageFailed(true)}
                className={`${dims.box} shrink-0 object-cover`}
            />
        );
    }

    // Mundur ke ikon (sudah mengikuti tone teks oleh SportIcon).
    if (sport?.iconUrl) {
        return (
            <span
                className={`app-content-subtle grid shrink-0 place-items-center ${dims.box}`}
            >
                <SportIcon sport={sport} className={dims.icon} />
            </span>
        );
    }

    return (
        <span
            className={`app-content-subtle app-text-primary grid shrink-0 place-items-center ${dims.box}`}
        >
            <Dumbbell className={dims.glyph} />
        </span>
    );
}
